import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDate, prettify } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PortalUpload } from "@/components/PortalUpload";
import { FileText, FileImage, Download, File as FileIcon } from "lucide-react";
import { toast } from "sonner";

const iconFor = (ct) => (ct?.startsWith("image/") ? FileImage : ct?.includes("pdf") ? FileText : FileIcon);

export default function PurchaserDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/portal/purchaser/documents"); setDocs(data.items || []); }
    catch (e) { if (e?.response?.status === 403) setGated(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const open = async (d) => {
    try {
      const res = await api.get(`/portal/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open file"); }
  };

  if (gated) {
    return <Card className="p-12 text-center border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="purchaser-docs-gated"><p className="font-medium text-[#252221]">Access pending approval</p><p className="text-sm text-muted-foreground">Documents unlock once your investor agreement is approved.</p></Card>;
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <h2 className="font-display text-2xl font-bold text-[#252221]">Documents</h2>
        <PortalUpload endpoint="/portal/purchaser/documents/upload" label="Upload offer document" testid="purchaser-documents-upload" onUploaded={load} />
      </div>
      <p className="text-sm text-muted-foreground mb-5">Documents shared with you by our team, plus offer documents you upload.</p>
      {loading ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><Skeleton className="h-16" /><Skeleton className="h-16" /></div> :
        docs.length === 0 ? <Card className="p-10 text-center text-muted-foreground border-[#E6EDF2]" data-testid="purchaser-documents-empty">No documents yet.</Card> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {docs.map((d) => {
              const I = iconFor(d.content_type);
              return (
                <div key={d.id} className="flex items-center gap-3 rounded-xl border border-[#E6EDF2] p-3 bg-white" data-testid="purchaser-document-card">
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}><I size={20} className="text-[#1C4358]" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate text-[#252221]">{d.original_filename}</div>
                    <div className="text-[11px] text-muted-foreground">{d.is_mine ? "Your upload" : "Shared by MovingMyMama"} · {formatDate(d.created_at)}</div>
                  </div>
                  <button onClick={() => open(d)} className="p-2 rounded-lg hover:bg-[rgba(28,67,88,0.08)] text-[#1C4358] transition-colors" data-testid="purchaser-document-download" aria-label="Download"><Download size={16} /></button>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
